import React from "react";
import { Formik, Form, Field } from "formik";
import { connect } from "react-redux";
import { Grid, Hidden } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { red } from "@material-ui/core/colors";
import {
  date,
  checkbox,
  text,
  phone,
  radio,
  dropdown,
} from "./renderers/renderers";
import renderSubmitButton from "./renderers/SubmitFormButton";
import { phoneValidator } from "./validators";
import * as Yup from "yup";
import { withSnackbar } from "notistack";
import {
  SET_PATIENT,
  ADD_PENDING_QUESTIONNAIRE_RESPONSE,
} from "../actions/actions";
import {
  createQuestionnaireResponseResource,
  isObjEmpty,
} from "../utils/resources";
import { submitQuestionnaireResponses } from "../utils/asyncServerRequests";
import { errorClass } from "./consts";
import QuestionnaireItem from "../components/QuestionnaireItem";
import PharmacySearchModal from "../components/PharmacySearchModal";
import { addConditionalItemsToQuestionnaire } from "./questionnaireConditionalFields";

const styles = theme => ({
  icon: {
    margin: theme.spacing.unit * 2,
  },
  iconHover: {
    margin: theme.spacing.unit * 2,
    ":hover": {
      color: red[800],
    },
  },
  paper: {
    minWidth: "fit-content",
    width: "80%",
  },
  select: {
    minWidth: "fit-content",
  },
  container: {
    flexGrow: 1,
    padding: theme.spacing.unit * 2,
  },
  error: {
    color: "red",
  },
  avatar: {
    width: "4em",
    height: "4em",
  },
  tooltip: {
    fontSize: "2em",
  },
});

Yup.addMethod(Yup.string, "phone", function() {
  return this.test("phoneNumber", "Invalid phone number", function(value) {
    if (value) {
      value = value.replace(/\D/g, "").toString();
      if (
        value.length !== 10 ||
        value.charAt(0) === "0" ||
        value.charAt(0) === "1"
      ) {
        return false;
      }
      return phoneValidator(value);
    } else {
      return true;
    }
  });
});

const QuestionnaireSchema = Yup.object().shape({
  //   firstName: Yup.string().required("Required"),
  //   lastName: Yup.string().required("Required"),
  //   email: Yup.string().email("Invalid email"),
  //   // .required("Required"),
  //   phoneNumber: Yup.string()
  //     .nullable()
  //     .phone(),
});

const isInsuranceOrderGroup = item => {
  if (
    !item ||
    item.type !== "group" ||
    !item.repeats ||
    !Array.isArray(item.item)
  ) {
    return false;
  }

  const groupText = `${item.definition || ""} ${item.text || ""}`.toLowerCase();

  if (!groupText.includes("insurance")) {
    return false;
  }

  return item.item.some(childItem => {
    const childText = `${childItem.linkId || ""} `
      .concat(childItem.text || "")
      .toLowerCase();

    return childText.includes("order");
  });
};

const getInsuranceOrderError = (questionnaireItem, questionnaireData) => {
  if (!isInsuranceOrderGroup(questionnaireItem)) {
    return "";
  }

  const orderItem = questionnaireItem.item.find(childItem => {
    const childText = `${childItem.linkId || ""} `
      .concat(childItem.text || "")
      .toLowerCase();

    return childText.includes("order");
  });
  const orderValues = questionnaireData[questionnaireItem.linkId];

  if (!orderItem || !Array.isArray(orderValues) || orderValues.length < 2) {
    return "";
  }

  const selectedOrders = new Set();

  for (const orderValueGroup of orderValues) {
    const orderValue = orderValueGroup && orderValueGroup[orderItem.linkId];
    const normalizedValue =
      orderValue && typeof orderValue === "object"
        ? orderValue.value
        : orderValue;
    const normalizedLabel =
      orderValue && typeof orderValue === "object" && orderValue.label
        ? orderValue.label
        : normalizedValue;

    if (!normalizedValue) {
      continue;
    }

    if (selectedOrders.has(normalizedValue)) {
      return `* ${questionnaireItem.text}: only one ${normalizedLabel} policy order is allowed`;
    }

    selectedOrders.add(normalizedValue);
  }

  return "";
};

const allowsNoRepeatedEntries = item => {
  return (
    item &&
    Array.isArray(item.extension) &&
    item.extension.some(
      extension =>
        extension &&
        extension.valueBoolean === true &&
        typeof extension.url === "string" &&
        (extension.url === "allow-none" ||
          extension.url.endsWith("/allow-none"))
    )
  );
};

const hasQuestionnaireValue = value => {
  if (Array.isArray(value)) {
    return value.some(entry => hasQuestionnaireValue(entry));
  }

  if (value == null) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return true;
  }

  if (typeof value === "object") {
    return Object.keys(value).some(key => {
      if (key === "__isNew__") {
        return false;
      }

      return hasQuestionnaireValue(value[key]);
    });
  }

  return false;
};

const hasGroupedItemResponse = (item, questionnaireData) => {
  if (!item || item.type === "display") {
    return false;
  }

  if (item.repeats) {
    return hasQuestionnaireValue(questionnaireData[item.linkId]);
  }

  if (item.type === "group" && Array.isArray(item.item) && item.item.length) {
    return item.item.some(subItem =>
      hasGroupedItemResponse(subItem, questionnaireData)
    );
  }

  return hasQuestionnaireValue(questionnaireData[item.linkId]);
};

const getRequiredGroupedItemError = (items, questionnaireData) => {
  if (!Array.isArray(items) || !items.length) {
    return "";
  }

  for (const item of items) {
    if (!item || item.type === "display") {
      continue;
    }

    if (item.required === true && (item.repeats || item.type === "group")) {
      if (!hasGroupedItemResponse(item, questionnaireData)) {
        return item.repeats
          ? `* ${item.text}: at least one response is required`
          : `* ${item.text}: this field is required`;
      }
    }

    if (item.repeats && item.type === "group" && Array.isArray(item.item)) {
      const repeatedValues = Array.isArray(questionnaireData[item.linkId])
        ? questionnaireData[item.linkId]
        : [];

      for (const groupValue of repeatedValues) {
        const nestedError = getRequiredGroupedItemError(
          item.item,
          groupValue || {}
        );
        if (nestedError) {
          return nestedError;
        }
      }
    } else if (
      item.type === "group" &&
      Array.isArray(item.item) &&
      item.item.length
    ) {
      const nestedError = getRequiredGroupedItemError(
        item.item,
        questionnaireData
      );
      if (nestedError) {
        return nestedError;
      }
    }
  }

  return "";
};

class QuestionnaireForm extends React.Component {
  constructor(props) {
    super(props);

    this.formik = null;

    this.state = {
      initialValues: this.getInitialValues(props.questionnaire.item),
      helpers: {},
      customErrorMessage: "",
      pharmacyModalOpen: false,
      pharmacyModalData: null,
      selectedPharmacy: null,
      pharmacyModalFieldName: null,
      selectedPharmacyFieldName: null,
    };

    this.addHelpIds = this.addHelpIds.bind(this);
  }

  componentDidMount() {
    this.getHelperValues(this.props.questionnaire.item);
    window.addEventListener("beforeunload", this.confirmLeave);

    // Set up global pharmacy modal function. We need this for trigger from other component.
    window.showPharmacyModal = pharmacyModalConfig => {
      this.showPharmacyModal(pharmacyModalConfig);
    };

    // Set up global functions for pharmacy selection. Need this to talk between components.
    window.getSelectedPharmacy = fieldName => {
      return this.getSelectedPharmacy(fieldName);
    };

    window.clearSelectedPharmacy = fieldName => {
      this.clearSelectedPharmacy(fieldName);
    };
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.confirmLeave);
    // Clean up
    window.showPharmacyModal = null;
    window.getSelectedPharmacy = null;
    window.clearSelectedPharmacy = null;
  }

  confirmLeave = e => {
    const result = window.confirm(
      "Any unsaved changes will be lost. Are you sure that you want to leave?"
    );
    if (!result) {
      e.preventDefault();
      e.returnValue = "";
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.questionnaire && this.props.questionnaire) {
      if (prevProps.questionnaire.id !== this.props.questionnaire.id) {
        this.setState({
          initialValues: this.getInitialValues(this.props.questionnaire.item),
        });
      }
    }
  }

  onKeyDown = keyEvent => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      if (keyEvent.target.tagName.toLowerCase() === "textarea") {
        return;
      }
      keyEvent.preventDefault();
    }
  };

  getHelperValues = item => {
    if (Array.isArray(item) && item.length > 0) {
      item.forEach((subItem, index) => {
        if (
          Array.isArray(subItem.item) &&
          subItem.item.length === 1 &&
          subItem.item[0].type === "display" &&
          Array.isArray(subItem.item[0].extension) &&
          subItem.item[0].extension.length === 1 &&
          subItem.item[0].extension[0].url ===
            "http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl" &&
          subItem.item[0].extension[0].valueCodeableConcept &&
          Array.isArray(
            subItem.item[0].extension[0].valueCodeableConcept.coding
          ) &&
          subItem.item[0].extension[0].valueCodeableConcept.coding.length ===
            1 &&
          subItem.item[0].extension[0].valueCodeableConcept.coding[0].code ===
            "help"
        ) {
          this.addHelpIds(subItem.item[0]);
        }
      });
    }
  };

  getInitialValues = item => {
    let linkIds = {};
    if (Array.isArray(item) && item.length > 0) {
      item.forEach((item, index) => {
        if (item.type !== "display") {
          if (item.repeats) {
            linkIds[item.linkId] = allowsNoRepeatedEntries(item)
              ? []
              : [this.getInitialValues(item.item)];
          } else if (Array.isArray(item.item) && item.item.length > 0) {
            if (item.item.length === 1 && item.item[0].type === "display") {
              linkIds[item.linkId] = "";
            } else {
              linkIds = Object.assign(
                linkIds,
                this.getInitialValues(item.item)
              );
            }
          } else {
            linkIds[item.linkId] = "";
          }
        }
      });
    }
    // console.debug("initial values", linkIds);
    return linkIds;
  };

  addHelpIds = item => {
    this.setState({
      helpers: {
        [item.linkId]: item.text,
      },
    });
  };

  getComponentForType = type => {
    switch (type) {
      case "text": {
        return text;
      }
      case "date": {
        return date;
      }
      case "checkbox": {
        return checkbox;
      }
      case "phone": {
        return phone;
      }
      case "radio": {
        return radio;
      }
      case "dropdown": {
        return dropdown;
      }
      default: {
        break;
      }
    }
  };

  showPharmacyModal = pharmacyModalConfig => {
    this.setState({
      pharmacyModalOpen: true,
      pharmacyModalData: pharmacyModalConfig
        ? pharmacyModalConfig.searchData
        : null,
      pharmacyModalFieldName: pharmacyModalConfig
        ? pharmacyModalConfig.fieldName
        : null,
    });
  };

  closePharmacyModal = () => {
    this.setState({
      pharmacyModalOpen: false,
      pharmacyModalData: null,
      pharmacyModalFieldName: null,
    });
  };

  getSelectedPharmacy = fieldName => {
    if (fieldName !== this.state.selectedPharmacyFieldName) {
      return null;
    }

    return this.state.selectedPharmacy;
  };

  handlePharmacySelection = selectedPharmacy => {
    const pharmacyId =
      selectedPharmacy && selectedPharmacy.id
        ? selectedPharmacy.id.toString()
        : "";
    const pharmacyFieldName = this.state.pharmacyModalFieldName;

    if (this.formik && pharmacyFieldName) {
      this.formik.setFieldValue(pharmacyFieldName, pharmacyId, true);
      this.formik.setFieldTouched(pharmacyFieldName, true, true);
    }

    this.setState(
      {
        selectedPharmacy: selectedPharmacy,
        selectedPharmacyFieldName: pharmacyFieldName,
        pharmacyModalFieldName: null,
      },
      () => {
        this.forceUpdate(); // Need this to show updates
      }
    );
  };

  clearSelectedPharmacy = fieldName => {
    const pharmacyFieldName = fieldName || this.state.selectedPharmacyFieldName;

    if (this.formik && pharmacyFieldName) {
      this.formik.setFieldValue(pharmacyFieldName, "", true);
      this.formik.setFieldTouched(pharmacyFieldName, true, true);
    }

    this.setState(
      {
        selectedPharmacy: null,
        selectedPharmacyFieldName: null,
      },
      () => {
        this.forceUpdate(); // Need this to show updates
      }
    );
  };

  addPharmacyDataToSubmission = questionnaireData => {
    if (!this.state.selectedPharmacy) {
      return questionnaireData;
    }

    const pharmacy = this.state.selectedPharmacy;
    questionnaireData["pharmacy-name"] = pharmacy.name || "";
    questionnaireData["pharmacy-phone"] = pharmacy.primary_phone_number || "";
    questionnaireData["pharmacy-address"] = pharmacy.address_line_1 || "";
    questionnaireData["pharmacy-city"] = pharmacy.address_city || "";
    questionnaireData["pharmacy-state"] = pharmacy.address_state || "";
    questionnaireData["pharmacy-zipcode"] = pharmacy.address_zipcode || "";
    questionnaireData["pharmacy-id"] = pharmacy.id
      ? pharmacy.id.toString()
      : "";

    return questionnaireData;
  };

  addPharmacyIdToQuestionnaire = questionnaire => {
    const modifiedQuestionnaire = JSON.parse(JSON.stringify(questionnaire)); // Deep copy
    const pharmacyGroup = modifiedQuestionnaire.item.find(
      item => item.definition === "pharmacy" && item.type === "group"
    );

    if (pharmacyGroup && Array.isArray(pharmacyGroup.item)) {
      // Remove pharmacy search button
      pharmacyGroup.item = pharmacyGroup.item.filter(
        item => item.linkId !== "pharmacy-search"
      );

      // Add pharmacy-id field if it doesn't exist
      const hasPharmacyId = pharmacyGroup.item.some(
        item => item.linkId === "pharmacy-id"
      );
      if (!hasPharmacyId) {
        pharmacyGroup.item.push({
          linkId: "pharmacy-id",
          text: "Pharmacy ID",
          type: "string",
        });
      }
    }

    return modifiedQuestionnaire;
  };

  handleSubmit = async (values, actions) => {
    try {
      let customErrorMsg = "";
      let questionnaireData = { ...values };
      let modifiedQuestionnaire = { ...this.props.questionnaire };
      const hasSelectedPharmacy = !!(
        this.state.selectedPharmacy && this.state.selectedPharmacy.id
      );

      if (hasSelectedPharmacy) {
        questionnaireData = this.addPharmacyDataToSubmission(questionnaireData);
        modifiedQuestionnaire = this.addPharmacyIdToQuestionnaire(
          modifiedQuestionnaire
        );
      }

      modifiedQuestionnaire = addConditionalItemsToQuestionnaire(
        modifiedQuestionnaire,
        questionnaireData
      );

      // REF 05/20/2026 - Grouped item validation. Uncomment when we want to enforce.
      // customErrorMsg = getRequiredGroupedItemError(
      //   modifiedQuestionnaire.item,
      //   questionnaireData
      // );

      // Custom validations
      const keys = Object.keys(questionnaireData);
      keys.forEach(key => {
        if (customErrorMsg.length < 1) {
          const questionnaireItem = this.props.questionnaire.item.find(
            item => item.linkId === key
          );
          const insuranceOrderError = getInsuranceOrderError(
            questionnaireItem,
            questionnaireData
          );

          if (insuranceOrderError) {
            customErrorMsg = insuranceOrderError;
            return;
          }

          const formProp = questionnaireData[key];
          if (Array.isArray(formProp)) {
            for (const childFormProp of formProp) {
              if (
                childFormProp["allergyReaction"] &&
                (isObjEmpty(childFormProp["allergy"]) ||
                  (typeof childFormProp["allergy"] === "string" &&
                    childFormProp["allergy"].trim().length < 1))
              ) {
                customErrorMsg =
                  "* You must enter an allergy if you enter an allergy reaction";
                break;
              }
            }
          }
        }
      });
      this.setState({
        customErrorMessage: customErrorMsg,
      });
      if (customErrorMsg.length > 0) {
        return false;
      }

      let resource = createQuestionnaireResponseResource(
        this.props.patient,
        modifiedQuestionnaire,
        questionnaireData,
        null // this.props.savedQuestionnaireAnswers
      );
      if (this.props.formBundle.allowPartialSubmission) {
        const response = await submitQuestionnaireResponses(
          [resource],
          this.props.formBundle,
          this.props.patient,
          this.props.newPatient
        );
        if (!(response && response.statusCode < 400)) {
          const message =
            response && response.error
              ? response.error
              : "Error submitting form";
          this.props.enqueueSnackbar(message, {
            variant: "error",
          });
          return;
        }
      } else {
        this.props.addPendingQuestionnaireResponses(resource);
      }
      await this.props.handleQuestionnaireSubmit();
    } catch (error) {
      const message =
        error && error.message
          ? error.message
          : error && error.error
          ? error.error
          : "Something went wrong submitting your form.";
      this.props.enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  render() {
    const { classes } = this.props;
    return (
      <React.Fragment>
        <Formik
          key={this.props.questionnaire.id}
          initialValues={this.state.initialValues}
          validationSchema={QuestionnaireSchema}
          onSubmit={this.handleSubmit}
          innerRef={form => {
            this.formik = form;
          }}
          className={classes.form}
          id="customForm"
        >
          {formProps => (
            <Form
              onKeyDown={this.onKeyDown}
              className="h-auto mx-auto w-full max-w-5xl bg-white shadow-md rounded px-2 py-2 pt-2 md:px-8 md:py-8 md:pt-8"
            >
              <div className="mx-auto w-full max-w-xl">
                <Grid container className={classes.container} spacing={16}>
                  <QuestionnaireItem
                    item={this.props.questionnaire.item}
                    formProps={formProps}
                    helpers={this.state.helpers}
                  />
                </Grid>
                <Hidden mdUp>
                  <Field
                    name="submit"
                    component={renderSubmitButton}
                    buttonWidth="100%"
                  >
                    Next
                  </Field>
                  {formProps.submitCount > 0 &&
                    formProps.errors &&
                    Object.keys(formProps.errors).length > 0 && (
                      <span className={errorClass}>
                        Please fix all errors & answer all required questions as
                        indicated by the *
                      </span>
                    )}
                  {this.state.customErrorMessage.length > 0 && (
                    <>
                      <span className={errorClass} style={{ display: "block" }}>
                        {this.state.customErrorMessage}
                      </span>
                    </>
                  )}
                </Hidden>
                <Hidden smDown>
                  <Field name="submit" component={renderSubmitButton}>
                    Next
                  </Field>
                  {formProps.submitCount > 0 &&
                    formProps.errors &&
                    Object.keys(formProps.errors).length > 0 && (
                      <span className={errorClass}>
                        Please fix all errors & answer all required questions as
                        indicated by the *
                      </span>
                    )}
                  {this.state.customErrorMessage.length > 0 && (
                    <>
                      <span className={errorClass} style={{ display: "block" }}>
                        {this.state.customErrorMessage}
                      </span>
                    </>
                  )}
                </Hidden>
              </div>
            </Form>
          )}
        </Formik>

        <PharmacySearchModal
          open={this.state.pharmacyModalOpen}
          onClose={this.closePharmacyModal}
          pharmacyData={this.state.pharmacyModalData}
          onSelectPharmacy={this.handlePharmacySelection}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => {
  return {
    organization: state.settings.organization,
    patient: state.patients.selectedPatient,
    newPatient: state.patients.newPatient,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setSelectedPatient: (custom, newPatient) =>
      dispatch({ type: SET_PATIENT, custom: custom, newPatient: newPatient }),
    addPendingQuestionnaireResponses: questionnaireResponse =>
      dispatch({
        type: ADD_PENDING_QUESTIONNAIRE_RESPONSE,
        pendingQuestionnaireResponse: questionnaireResponse,
      }),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withSnackbar(QuestionnaireForm)));
